// Where a person stands on MotionSafe's legal documents, on the People page
// (ARCHITECTURE.md §16): every document in effect accepted, covered by the
// organization's custom agreement, or not yet, in which case they are asked
// at their next full sign-in. The row marks what is done (a check) or
// covered, and leaves the rest empty; the person's details say it per
// document, with the version and when.

import { Text } from '@mantine/core';
import type { LegalStanding, ListLegalStandingResponse } from '@motionsafe/ms-console-api/admin/v1/legal_pb.js';
import { IconCheck } from '@tabler/icons-react';

import { day } from '../lib/format';
import { Facts } from './page';

/** The person's row of the standing, by id. */
export function standingOf(data: ListLegalStandingResponse | undefined, userId: string): LegalStanding | undefined {
  return data?.people.find((p) => p.userId === userId);
}

// Nobody in an organization with an agreement is asked, so nobody there is "not yet".
const covered = (data: ListLegalStandingResponse) => !!data.agreement;

/** The row's mark: a check when every document in effect is accepted, else nothing. */
export function LegalCell({ data, standing }: { data: ListLegalStandingResponse; standing: LegalStanding | undefined }) {
  if (data.documents.length === 0) return null;
  if (covered(data)) return <Text size="sm" c="dimmed" title={`Covered by the organization's agreement (${data.agreement!.title})`}>Covered</Text>;
  const acceptedAll = !!standing && data.documents.every((d) => standing.acceptances.some((a) => a.documentId === d.id && a.current));
  if (!acceptedAll) return null;
  return <span title="Accepted every document in effect" style={{ display: 'inline-flex' }}><IconCheck size={18} stroke={2} color="var(--mantine-color-success-text)" role="img" aria-label="Accepted every document in effect" /></span>;
}

/** The details' section: per document, what they accepted and when, or that the agreement covers them. */
export function LegalStandingFacts({ data, standing }: { data: ListLegalStandingResponse; standing: LegalStanding | undefined }) {
  if (covered(data)) {
    const a = data.agreement!;
    return <Text size="sm">Covered by the organization&rsquo;s agreement ({a.title}), since {day(a.coversFrom)}. Nobody in the organization is asked to accept MotionSafe&rsquo;s documents while it stands.</Text>;
  }
  if (data.documents.length === 0) return <Text size="sm" c="dimmed">MotionSafe has no document in effect, so nothing is asked.</Text>;
  return (
    <Facts rows={data.documents.map((d) => {
      const accepted = standing?.acceptances.find((a) => a.documentId === d.id);
      const asked = standing?.outstandingDocumentIds.includes(d.id) ?? true;
      const where = accepted?.surface === 'device' ? ' on a device' : accepted?.surface === 'console' ? ' on the console' : '';
      const value = accepted && !asked
        ? `Version ${accepted.version}, accepted ${day(accepted.acceptedAt)}${where}`
        : accepted
          ? `Version ${accepted.version} accepted ${day(accepted.acceptedAt)}; version ${d.requiredVersion || d.inEffect?.version || ''} asked at next sign-in`
          : 'Not yet: asked at next sign-in';
      return { label: d.title, value };
    })} />
  );
}
